/* Tork SEO — listicle template (best-of / roundup, built to the AI-citation format).
 * Bakes in the 5 citation rules: "Best X" framing, standalone TL;DR answer, one verifiable
 * standout per pick, a visible methodology, and a comparison table + FAQ. Honest roundup —
 * Tork is the Editor's Pick #1 on the stated criteria, among real competitors with real facts.
 */
import type { Page, Block } from "../../components/tork/TorkBlocks";
import { TOOLS, toolByKey, type Tool } from "./tools";

const ORIGIN = "https://tork.digitaldomination.ai";
export const LISTICLE_YEAR = "2026"; // bump once a year (single source)

export type Listicle = {
  slug: string;            // path under /best/
  h1: string;
  query: string;           // the literal "best ..." phrase people/models type
  metaTitle: string;
  metaDescription: string;
  answer: string;          // standalone, quotable answer (markdown)
  intro?: string;          // optional extra context paragraph(s)
  criteria: string[];      // "how we ranked these" bullets
  toolKeys: string[];      // ranking order (keys from tools.ts)
  faq: { q: string; a: string }[];
  breadcrumbName: string;
};

export function buildListiclePage(t: Listicle): Page {
  const slug = "/best/" + t.slug;
  const picks: Tool[] = t.toolKeys.map(toolByKey);

  const rankedItems = picks.map((p, i) => ({
    rank: i + 1,
    name: p.name,
    badge: p.isClient ? "Editor's Pick" : undefined,
    tagline: p.tagline,
    bestFor: p.bestFor,
    standout: p.standout,
    price: p.price,
    href: p.href,
  }));

  const blocks: Block[] = [
    { type: "hero", h1: t.h1, subhead: t.metaDescription, primaryCta: "Try the #1 pick for $1", riskReversal: "Tork: $1 for 3 days. 10x value or cancel in one click." },
    { type: "trustBar" },
    { type: "answerBox", md: t.answer },
    ...(t.intro ? [{ type: "prose", md: t.intro } as Block] : []),
    { type: "prose", heading: "How we ranked these", md: `We evaluated each tool on the criteria that matter for ${t.query.replace(/^best /, "")}:\n\n${t.criteria.map((c) => "- " + c).join("\n")}\n\nThis is an independent roundup. Tork is published by Digital Domination and earns the Editor's Pick on these criteria — but where a competitor genuinely fits a different buyer better, we say so.` },
    { type: "rankedList", items: rankedItems },
    { type: "table", caption: "At-a-glance comparison", headers: ["Tool", "Best for", "Starting price", "Model"], rows: picks.map((p) => [p.isClient ? `**${p.name}**` : p.name, p.bestFor, p.price, p.model]) },
    { type: "ctaBand", headline: "The #1 pick is the lowest-risk to try.", body: "Tork starts at $1 for 3 days — see it work on your own profile before you commit.", ctaLabel: "Try Tork for $1" },
    { type: "faq", items: t.faq },
    { type: "relatedLinks", links: [
      { label: "All best-of guides", href: "/best" },
      { label: "AI local SEO software", href: "/ai-local-seo-software" },
      { label: "Tork pricing", href: "/pricing" },
      { label: "Tork reviews", href: "/reviews" },
    ] },
  ];

  return {
    slug,
    title: t.metaTitle,
    description: t.metaDescription,
    blocks,
    breadcrumb: [{ name: "Best-of guides", href: "/best" }, { name: t.breadcrumbName, href: slug }],
    jsonLd: [
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Best-of guides", item: ORIGIN + "/best" },
        { "@type": "ListItem", position: 2, name: t.breadcrumbName, item: ORIGIN + slug },
      ] },
      { "@context": "https://schema.org", "@type": "ItemList", name: t.h1, itemListOrder: "https://schema.org/ItemListOrderDescending", numberOfItems: picks.length, itemListElement: picks.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, description: p.tagline })) },
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: t.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  };
}

export const DEFAULT_TOOL_KEYS = TOOLS.map((x) => x.key);
